/**
 * WebView 環境での自動ログイン
 *
 * INVALID_TOKEN (code 3) 発生時に Native の Keychain から認証情報を取得し、
 * 裏でログイン API を叩いてトークンを差し替える。
 * 失敗した場合は false を返し、呼び出し元がログイン画面へ遷移させる。
 *
 * 同時に複数の code 3 が発生しても、自動ログインは 1 回だけ実行される（singleton promise）。
 * 自動ログイン成功後のリトライでも code 3 が返った場合、以降の試行を抑止する（exhausted フラグ）。
 * フラグはメモリ上のみで、ページリロードでリセットされる。
 */

import { WEB_APPLICATION_NAME } from "~/constants/app";
import type { LoginResponse } from "~/features/auth/types";
import i18n from "~/i18n";
import { getCurrentApplicationId } from "~/lib/applicationId";
import { resolveScFlag } from "~/lib/scFlag";
import { useAuthStore } from "~/stores/useAuthStore";
import { API_ERROR_CODES, type ApiResponse } from "~/types/api";
import { toGender, type User } from "~/types/user";
import { type DeviceCheckResult, native } from "./nativeBridge";
import { isWebView } from "./webview";

const BASE_URL = import.meta.env.DEV ? "/api" : import.meta.env.VITE_API_URL;

/** 進行中の自動ログイン Promise（重複防止） */
let pendingAutoLogin: Promise<boolean> | null = null;

/** 自動ログインが成功したのにリトライでも code 3 だった場合に true にする */
let exhausted = false;

/**
 * 自動ログインを試行する。
 * @returns 成功なら true、失敗（Native 非環境・認証情報なし・API 失敗・exhausted）なら false
 */
export const attemptAutoLogin = (): Promise<boolean> => {
  if (exhausted) {
    return Promise.resolve(false);
  }
  if (pendingAutoLogin) {
    return pendingAutoLogin;
  }

  pendingAutoLogin = executeAutoLogin().finally(() => {
    pendingAutoLogin = null;
  });

  return pendingAutoLogin;
};

/**
 * 自動ログインが無意味だったことを記録する。
 * apiClient がリトライ後も INVALID_TOKEN を受け取った場合に呼ぶ。
 */
export const markAutoLoginExhausted = (): void => {
  exhausted = true;
};

/**
 * exhausted フラグをリセットする（テスト用）。
 */
export const resetAutoLoginState = (): void => {
  exhausted = false;
  pendingAutoLogin = null;
};

export const resolveDeviceId = (deviceCheck: {
  result: DeviceCheckResult | null;
}): string | undefined => {
  if (!deviceCheck.result) return undefined;
  if (deviceCheck.result.platform === "ios") return deviceCheck.result.idfv;
  return deviceCheck.result.android_original_id || undefined;
};

export const formatLoginTime = (): string => {
  const now = new Date();
  return `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}${String(now.getHours()).padStart(2, "0")}${String(now.getMinutes()).padStart(2, "0")}${String(now.getSeconds()).padStart(2, "0")}`;
};

const executeAutoLogin = async (): Promise<boolean> => {
  if (!isWebView()) return false;

  // sendMessageToWebView は同時に 1 件しか処理できないため逐次呼び出しする
  const authInfo = await native.requestAuthInfo();
  const deviceCheck = await native.getDeviceCheck();

  if (!authInfo) return false;

  const platform = deviceCheck.result?.platform;
  const deviceId = resolveDeviceId(deviceCheck);

  const requestBody = {
    api: "login_for_web",
    email: authInfo.email,
    pwd: authInfo.pwd,
    application_id: authInfo.applicationId ?? getCurrentApplicationId(),
    ...(deviceId ? { device_id: deviceId } : {}),
    login_time: formatLoginTime(),
    machine_type: platform ?? "web",
    language: i18n.language,
    web_application: WEB_APPLICATION_NAME,
  };

  try {
    const response = await fetch(BASE_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) return false;

    const json = (await response.json()) as ApiResponse<LoginResponse>;
    if (json.code !== API_ERROR_CODES.SUCCESS) return false;

    const data = json.data;
    const user: User = {
      id: data.user_id,
      name: data.user_name,
      email: data.email,
      gender: toGender(data.gender),
      age: data.age,
      avatarUrl: data.ava_id || null,
      region: data.region,
    };

    useAuthStore.getState().setAuth(data.token, data.user_id, user);
    const scFlag = await resolveScFlag(authInfo.scFlag);
    native.notifyLoginSuccess({
      email: authInfo.email,
      pwd: authInfo.pwd,
      userId: data.user_id,
      token: data.token,
      scFlag,
    });
    return true;
  } catch {
    return false;
  }
};
